import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AvatarGroup } from './AvatarGroup';
import ProgressBar from './ProgressBar';
import ProjectTaskDetail from './ProjectTaskDetail';
import TaskStatusDropdown from './TaskStatusDropdown';
import CustomButton from '../OnboardingComponents/CustomButton';
import CustomCheckbox from '../OnboardingComponents/CustomCheckbox';
import EmptyState from '../Common/EmptyState';
import TruncatedText from '../Common/TruncatedText';
import StartSessionDropdown from './StartSessionDropdown';
import { ICONS } from '../../constants/icons';
import {
  resolveTaskSessionAction,
  taskSessionActionLabel,
} from '../../helpers/session';
import { formatWorkedHours } from '../../helpers/sessionDetail';
import { celebrateTaskCompleted } from './TaskCompletedCelebration';

const SKELETON_GROUPS = 3;
const SKELETON_ROWS = 2;

const TaskRowSkeleton = () => (
  <div className="project-task-table__row project-task-table__row--skeleton" role="row">
    <div className="project-task-table__cell project-task-table__cell--name" role="cell">
      <span className="project-task-table__skeleton-check" />
      <span className="project-task-table__skeleton-bar project-task-table__skeleton-bar--title" />
      <span className="project-task-table__skeleton-bar project-task-table__skeleton-bar--progress" />
    </div>
    <div className="project-task-table__cell project-task-table__cell--status" role="cell">
      <span className="project-task-table__skeleton-bar project-task-table__skeleton-bar--status" />
    </div>
    <div className="project-task-table__cell project-task-table__cell--collab" role="cell">
      <span className="project-task-table__skeleton-avatars" aria-hidden="true">
        <span className="project-task-table__skeleton-avatar" />
        <span className="project-task-table__skeleton-avatar" />
        <span className="project-task-table__skeleton-avatar" />
      </span>
    </div>
    <div className="project-task-table__cell project-task-table__cell--date" role="cell">
      <span className="project-task-table__skeleton-bar project-task-table__skeleton-bar--date" />
    </div>
    <div className="project-task-table__cell project-task-table__cell--hours" role="cell">
      <span className="project-task-table__skeleton-bar project-task-table__skeleton-bar--hours" />
    </div>
    <div className="project-task-table__cell project-task-table__cell--hours" role="cell">
      <span className="project-task-table__skeleton-bar project-task-table__skeleton-bar--hours" />
    </div>
    <div className="project-task-table__cell project-task-table__cell--action" role="cell">
      <span className="project-task-table__skeleton-bar project-task-table__skeleton-bar--session" />
    </div>
  </div>
);

/**
 * ProjectTaskTable
 * ------------------------------------------------------------------
 * Grouped All Tasks table:
 * Task Name | Status | Collaborators | Due Date | Estimated Hours | Logged Hrs | Start Session
 * (Progress bar sits beside the title, per Figma 40004192-3493)
 */
const ProjectTaskTable = ({
  groups = [],
  isLoading = false,
  error = null,
  playIconSrc = ICONS.play,
  plusIconSrc = ICONS.plus,
  chevronIconSrc = ICONS.chevronDown,
  arrowIconSrc = ICONS.arrowRightBlack,
  checkIconSrc = ICONS.checkGreen,
  onAddTask,
  onStartSession,
  onStatusChange,
  onViewTasks,
  onOpenTask,
  onCollaboratorsAdded,
  onTaskUpdated,
  onTaskDeleted,
}) => {
  const currentUser = useSelector((state) => state.auth.currentUser);
  const currentUserId = currentUser?.id ? String(currentUser.id) : '';

  const [checked, setChecked] = useState(new Set());
  const [expanded, setExpanded] = useState(() => new Set(groups.map((g) => g.id)));
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setExpanded(new Set(groups.map((group) => group.id)));
  }, [groups]);

  useEffect(() => {
    const completedIds = groups.flatMap((group) =>
      (group.tasks || [])
        .filter((task) => task.completed || task.status === 'Completed')
        .map((task) => task.id)
    );
    setChecked(new Set(completedIds));
  }, [groups]);

  const isTaskCompleted = (task) =>
    checked.has(task.id) ||
    task.action === 'done' ||
    task.completed ||
    task.status === 'Completed';

  useEffect(() => {
    setSelected((prev) => {
      if (!prev?.task?.id) return prev;
      for (const group of groups) {
        const found = (group.tasks || []).find(
          (task) => String(task.id) === String(prev.task.id)
        );
        if (found) {
          return {
            ...prev,
            task: found,
            owner: group.owner || prev.owner,
          };
        }
      }
      return prev;
    });
  }, [groups]);

  const isCurrentUserTaskCollaborator = (task) =>
    currentUserId &&
    (task.collaborators || []).some(
      (c) => String(c.userId || c.user?.id || c.id) === currentUserId
    );

  const canEditTask = (task, isOwner = false) =>
    Boolean(
      currentUserId &&
        onStatusChange &&
        (isOwner || task.owner?.isYou || isCurrentUserTaskCollaborator(task))
    );

  const toggleChecked = (task, group, nextChecked) => {
    if (!canEditTask(task, group?.owner?.isYou)) return;
    const isNowChecked =
      typeof nextChecked === 'boolean' ? nextChecked : !isTaskCompleted(task);
    setChecked((prev) => {
      const next = new Set(prev);
      if (isNowChecked) next.add(task.id);
      else next.delete(task.id);
      return next;
    });
    if (isNowChecked) {
      celebrateTaskCompleted()
    }
    onStatusChange?.(task, isNowChecked ? 'Completed' : 'Not Started', group);
  };

  const toggleGroup = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openTask = (task, owner) => {
    if (onOpenTask) {
      onOpenTask(task, owner);
      return;
    }
    setSelected({ task, owner });
  };

  return (
    <div
      className="project-task-table"
      role="table"
      aria-label="Project tasks"
      aria-busy={isLoading}
    >
      <div className="project-task-table__head" role="row">
        <div className="project-task-table__cell project-task-table__cell--name" role="columnheader">
          Task Name
        </div>
        <div className="project-task-table__cell project-task-table__cell--status" role="columnheader">
          Status
        </div>
        <div className="project-task-table__cell project-task-table__cell--collab" role="columnheader">
          Collaborators
        </div>
        <div className="project-task-table__cell project-task-table__cell--date" role="columnheader">
          Due Date
        </div>
        <div className="project-task-table__cell project-task-table__cell--hours" role="columnheader">
          Estimated Hours
        </div>
        <div className="project-task-table__cell project-task-table__cell--hours" role="columnheader">
          Logged Hrs
        </div>
        <div className="project-task-table__cell project-task-table__cell--action" role="columnheader">
          Start Session
        </div>
      </div>

      {isLoading && (
        <div aria-busy="true" aria-live="polite">
          {Array.from({ length: SKELETON_GROUPS }).map((_, groupIdx) => (
            <div
              key={`skeleton-group-${groupIdx}`}
              className="project-task-table__group"
              aria-hidden="true"
            >
              <div className="project-task-table__group-header project-task-table__group-header--skeleton">
                <div className="project-task-table__skeleton-owner">
                  <span className="project-task-table__skeleton-bar project-task-table__skeleton-bar--chevron" />
                  <span className="project-task-table__skeleton-bar project-task-table__skeleton-bar--owner" />
                  <span className="project-task-table__skeleton-bar project-task-table__skeleton-bar--role" />
                </div>
                <div className="project-task-table__skeleton-group-actions">
                  <span className="project-task-table__skeleton-bar project-task-table__skeleton-bar--btn" />
                  <span className="project-task-table__skeleton-bar project-task-table__skeleton-bar--btn-sm" />
                </div>
              </div>
              {Array.from({ length: SKELETON_ROWS }).map((__, rowIdx) => (
                <TaskRowSkeleton key={`skeleton-row-${groupIdx}-${rowIdx}`} />
              ))}
            </div>
          ))}
        </div>
      )}

      {!isLoading && error && (
        <div className="project-task-table__empty">
          <EmptyState
            title="Unable to load tasks"
            description={error}
          />
        </div>
      )}

      {!isLoading && !error && groups.length === 0 && (
        <div className="project-task-table__empty">
          <EmptyState
            title="No tasks yet"
            description="Add a task to get started."
            actionText={onAddTask ? 'Add Task' : undefined}
            actionTrailingIconSrc={onAddTask ? plusIconSrc : undefined}
            onActionClick={onAddTask}
            size="sm"
          />
        </div>
      )}

      {!isLoading &&
        !error &&
        groups.map((group) => {
        const isOpen = expanded.has(group.id);
        const roleTone = group.owner.roleTone || 'purple';
        const canStartSession = Boolean(group.owner?.isYou);

        return (
          <div key={group.id} className="project-task-table__group" role="rowgroup">
            <div className="project-task-table__group-header">
              <button
                type="button"
                className="project-task-table__group-toggle"
                onClick={() => toggleGroup(group.id)}
                aria-expanded={isOpen}
              >
                <img
                  src={chevronIconSrc}
                  alt=""
                  className={`project-task-table__chevron ${
                    isOpen ? '' : 'project-task-table__chevron--collapsed'
                  }`}
                />
                <span className="project-task-table__owner-name">
                  {group.owner.name}
                  {group.owner.isYou ? ' (You)' : ''}
                </span>
                <span className={`project-task-table__role project-task-table__role--${roleTone}`}>
                  {group.owner.role}
                </span>
              </button>

              <div className="project-task-table__group-actions">
                {canStartSession ? (
                  <CustomButton
                    variant="primary"
                    size="sm"
                    trailingIconSrc={plusIconSrc}
                    trailingIconAlt=""
                    className="project-task-table__btn-add-task"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onAddTask?.(group);
                    }}
                  >
                    Add Task
                  </CustomButton>
                ) : null}
                <CustomButton
                  variant="ghost"
                  size="sm"
                  trailingIconSrc={arrowIconSrc}
                  trailingIconAlt=""
                  className="project-task-table__btn-view"
                  onClick={() => onViewTasks?.(group)}
                >
                  View
                </CustomButton>
              </div>
            </div>

            {isOpen && (
              <>
                {group.tasks.length === 0 ? (
                  <div className="project-task-table__empty-group">
                    <EmptyState
                      title="No tasks yet"
                      description="Add a task to get started."
                      actionText={canStartSession && onAddTask ? 'Add Task' : undefined}
                      actionTrailingIconSrc={
                        canStartSession && onAddTask ? plusIconSrc : undefined
                      }
                      onActionClick={
                        canStartSession && onAddTask
                          ? () => onAddTask(group)
                          : undefined
                      }
                      size="sm"
                    />
                  </div>
                ) : null}
                {group.tasks.map((task) => (
                  <div
                    key={task.id}
                    className="project-task-table__row"
                    role="row"
                    onClick={() => openTask(task, group.owner)}
                  >
                    <div
                      className="project-task-table__cell project-task-table__cell--name"
                      role="cell"
                    >
                      <span
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <CustomCheckbox
                          checked={isTaskCompleted(task)}
                          onChange={({ target }) =>
                            toggleChecked(task, group, target.checked)
                          }
                          checkIconSrc={checkIconSrc}
                          className="project-task-table__checkbox"
                          disabled={!canEditTask(task, group?.owner?.isYou)}
                          aria-label={
                            canEditTask(task, group?.owner?.isYou)
                              ? `Mark ${task.title} complete`
                              : task.title
                          }
                        />
                      </span>
                      <TruncatedText
                        as="button"
                        type="button"
                        className="project-task-table__title"
                        title={task.title}
                        onClick={(e) => {
                          e.stopPropagation();
                          openTask(task, group.owner);
                        }}
                      >
                        {task.title}
                      </TruncatedText>
                      <ProgressBar
                        value={task.progress}
                        max={100}
                        tone="green"
                        className="project-task-table__progress"
                      />
                    </div>

                    <div
                      className="project-task-table__cell project-task-table__cell--status"
                      role="cell"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <TaskStatusDropdown
                        value={task.status || 'Not Started'}
                        onChange={
                          canEditTask(task, group?.owner?.isYou)
                            ? (nextStatus) => onStatusChange(task, nextStatus, group)
                            : undefined
                        }
                      />
                    </div>

                    <div className="project-task-table__cell project-task-table__cell--collab" role="cell">
                      {(task.collaborators || []).length > 0 ? (
                        <AvatarGroup
                          people={task.collaborators}
                          size="xs"
                          max={3}
                          total={task.totalCollaborators}
                          className="project-task-table__avatars"
                        />
                      ) : (
                        '—'
                      )}
                    </div>

                    <div className="project-task-table__cell project-task-table__cell--date" role="cell">
                      {task.dueDate}
                    </div>

                    <div className="project-task-table__cell project-task-table__cell--hours" role="cell">
                      {formatWorkedHours(task.estimated)}
                    </div>

                    <div className="project-task-table__cell project-task-table__cell--hours" role="cell">
                      {formatWorkedHours(task.logged)}
                    </div>

                    <div
                      className="project-task-table__cell project-task-table__cell--action"
                      role="cell"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {isTaskCompleted(task) ? (
                        <span className="project-task-table__done">Task Done</span>
                      ) : canStartSession ? (
                        (() => {
                          const sessionAction = resolveTaskSessionAction(task);
                          return (
                            <StartSessionDropdown
                              label={taskSessionActionLabel(sessionAction)}
                              playIconSrc={playIconSrc}
                              className="project-task-table__btn-session"
                              skipDropdown={sessionAction !== 'start'}
                              onSelect={(type) => onStartSession?.(task, type)}
                            />
                          );
                        })()
                      ) : null}
                    </div>
                  </div>
                ))}

                {canStartSession ? (
                  <button
                    type="button"
                    className="project-task-table__add"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onAddTask?.(group);
                    }}
                  >
                    <img src={plusIconSrc} alt="" aria-hidden="true" />
                    Add task...
                  </button>
                ) : null}
              </>
            )}
          </div>
        );
      })}

      <ProjectTaskDetail
        task={selected?.task}
        owner={selected?.owner}
        onClose={() => setSelected(null)}
        onStartSession={
          selected?.owner?.isYou &&
          selected?.task &&
          selected.task.action !== 'done' &&
          !selected.task.completed &&
          selected.task.status !== 'Completed'
            ? (type) => {
                onStartSession?.(selected.task, type);
                setSelected(null);
              }
            : undefined
        }
        onStatusChange={
          selected?.task &&
          canEditTask(selected.task, selected?.owner?.isYou) &&
          onStatusChange
            ? (nextStatus) =>
                onStatusChange(selected.task, nextStatus, {
                  owner: selected.owner,
                })
            : undefined
        }
        onCollaboratorsAdded={onCollaboratorsAdded}
        onTaskUpdated={(taskId, patchFields) => {
          setSelected((prev) =>
            prev && String(prev.task?.id) === String(taskId)
              ? { ...prev, task: { ...prev.task, ...patchFields } }
              : prev
          );
          onTaskUpdated?.(taskId, patchFields);
        }}
        onTaskDeleted={(taskId) => {
          onTaskDeleted?.(taskId);
          setSelected(null);
        }}
      />
    </div>
  );
};

export default ProjectTaskTable;
